import type { EcoRitmoExercise } from './ecoRitmoConfig';
import {
  allAttacksResolved,
  buildPatternTimeline,
  finalizeExpiredAttacks,
  summarizeAttackScore,
  type RhythmPatternTimeline,
} from './rhythmPattern';
import {
  attackHardEndMs,
  attackWindowEndMs,
  isInsideAttackWindow,
  type RhythmAttackEvent,
} from './rhythmTiming';
import { isRestToken } from './rhythmTokens';

export type TapVerdict = 'hit' | 'late' | 'early_fault' | 'rest_fault' | 'ignored';

export interface TapResult {
  verdict: TapVerdict;
  event?: RhythmAttackEvent;
  timingErrorMs?: number;
  finished: boolean;
  score: number;
}

export class RhythmTapEngine {
  readonly def: EcoRitmoExercise;
  readonly timeline: RhythmPatternTimeline;
  private attacks: RhythmAttackEvent[];
  private phaseStartMs = 0;
  private userActive = false;
  private faultCount = 0;
  private maxFaults: number;

  constructor(def: EcoRitmoExercise, maxFaults = 3) {
    this.def = def;
    this.timeline = buildPatternTimeline(def);
    this.attacks = this.timeline.attacks.map((a) => ({ ...a, status: 'pending', timingErrorMs: null }));
    this.maxFaults = maxFaults;
  }

  resetUserAttempts(): void {
    this.attacks = this.timeline.attacks.map((a) => ({ ...a, status: 'pending', timingErrorMs: null }));
    this.faultCount = 0;
    this.userActive = false;
    this.phaseStartMs = 0;
  }

  beginUserPhase(clockMs: number): void {
    this.userActive = true;
    this.phaseStartMs = clockMs;
  }

  getElapsedMs(clockMs: number): number {
    return Math.max(0, clockMs - this.phaseStartMs);
  }

  getAttacks(): readonly RhythmAttackEvent[] {
    return this.attacks;
  }

  getFaultCount(): number {
    return this.faultCount;
  }

  tick(clockMs: number): TapResult | null {
    if (!this.userActive) return null;
    const tMs = this.getElapsedMs(clockMs);
    finalizeExpiredAttacks(this.attacks, tMs);
    if (allAttacksResolved(this.attacks)) {
      this.userActive = false;
      return { verdict: 'ignored', finished: true, score: summarizeAttackScore(this.attacks) };
    }
    if (this.faultCount >= this.maxFaults) {
      this.userActive = false;
      return { verdict: 'ignored', finished: true, score: summarizeAttackScore(this.attacks) };
    }
    return null;
  }

  handleTap(clockMs: number): TapResult {
    if (!this.userActive) {
      return { verdict: 'ignored', finished: false, score: summarizeAttackScore(this.attacks) };
    }

    const tMs = this.getElapsedMs(clockMs);

    for (const slot of this.timeline.slots) {
      if (!slot.isRest) continue;
      if (tMs >= slot.startMs && tMs < slot.startMs + slot.durationMs) {
        this.faultCount++;
        if (this.faultCount >= this.maxFaults || allAttacksResolved(this.attacks)) {
          this.userActive = false;
        }
        return {
          verdict: 'rest_fault',
          finished: !this.userActive,
          score: summarizeAttackScore(this.attacks),
        };
      }
    }

    const pending = this.attacks.filter((a) => a.status === 'pending');
    const matched = pending.find((ev) => isInsideAttackWindow(ev, tMs));

    if (!matched) {
      this.faultCount++;
      if (this.faultCount >= this.maxFaults) this.userActive = false;
      return {
        verdict: 'early_fault',
        finished: !this.userActive,
        score: summarizeAttackScore(this.attacks),
      };
    }

    const isLate = tMs > attackWindowEndMs(matched);
    matched.status = isLate ? 'late' : 'hit';
    matched.timingErrorMs = tMs - matched.startMs;

    finalizeExpiredAttacks(this.attacks, tMs);

    const finished =
      allAttacksResolved(this.attacks) ||
      this.faultCount >= this.maxFaults ||
      tMs > this.timeline.totalMs + 800;

    if (finished) this.userActive = false;

    return {
      verdict: isLate ? 'late' : 'hit',
      event: matched,
      timingErrorMs: matched.timingErrorMs ?? undefined,
      finished,
      score: summarizeAttackScore(this.attacks),
    };
  }

  cellIndexAt(tMs: number): number {
    for (let i = this.timeline.slots.length - 1; i >= 0; i--) {
      const s = this.timeline.slots[i]!;
      if (tMs >= s.startMs) return s.cellIndex;
    }
    return 0;
  }

  isUserActive(): boolean {
    return this.userActive;
  }

  passThreshold(): boolean {
    const min = this.def.passScore ?? 65;
    return summarizeAttackScore(this.attacks) >= min && this.faultCount < this.maxFaults;
  }
}

export function attackShouldSound(token: string): boolean {
  return !isRestToken(token);
}

export { attackHardEndMs, attackWindowEndMs };
